import { Link } from "react-router-dom";
import { Sparkle, MoonStar, Compass } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

export default function Landing() {
    return (
        <div className="min-h-screen bg-sand-50 text-ink">
            <header className="sp-container flex items-center justify-between h-20">
                <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
                    <Sparkle strokeWidth={1.25} className="w-5 h-5 text-planet-sun" />
                    <span className="font-serif text-2xl">StarPath</span>
                </Link>
                <div className="flex items-center gap-2 sm:gap-3">
                    <ThemeToggle />
                    <Link to="/login" data-testid="landing-login" className="text-sm text-ink-muted hover:text-ink transition-colors px-2">Sign in</Link>
                    <Link to="/register" data-testid="landing-register" className="sp-btn-primary">Begin</Link>
                </div>
            </header>

            <section className="relative">
                <div className="sp-container grid lg:grid-cols-12 gap-10 lg:gap-12 items-center pt-8 sm:pt-12 pb-16 sm:pb-24">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="sp-overline mb-4 sm:mb-6">A celestial travel atlas</div>
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif leading-[1.05] tracking-tight">
                            Travel where the planets <em className="italic text-planet-sun">already</em> want you to be.
                        </h1>
                        <p className="mt-6 sm:mt-8 text-base sm:text-lg text-ink-soft max-w-xl leading-relaxed">
                            StarPath reads your natal chart and the current sky to suggest destinations and travel
                            windows that match the season of your life.
                        </p>
                        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                            <Link to="/register" data-testid="landing-cta-primary" className="sp-btn-primary">Build my chart</Link>
                            <Link to="/login" data-testid="landing-cta-secondary" className="sp-btn-ghost">I already have an account</Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative rounded-3xl overflow-hidden border border-ink/10">
                            <img
                                src="https://images.unsplash.com/photo-1486314030120-d5ab85fe58cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxkZXNlcnQlMjBza3klMjBkYXduJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4MDYxNjEzN3ww&ixlib=rb-4.1.0&q=85"
                                alt="Desert dawn"
                                className="w-full aspect-[4/5] object-cover"
                            />
                            <div className="absolute inset-x-6 bottom-6 bg-surface/85 backdrop-blur p-5 rounded-2xl border border-ink/10">
                                <div className="sp-overline">Tonight&apos;s sky</div>
                                <div className="font-serif text-xl mt-1">Moon in Cancer · Venus rising</div>
                                <div className="text-sm text-ink-muted mt-1">A soft week for water cities and quiet returns.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-t border-ink/10 bg-sand-100/60">
                <div className="sp-container py-20 grid md:grid-cols-3 gap-10">
                    {[
                        { icon: Sparkle, title: "Your natal chart, refined", body: "Sun, Moon and Rising calculated from your birth time and place — the trinity that shapes how you travel." },
                        { icon: MoonStar, title: "Season-aware advice", body: "What works this week wouldn't work next month. Recommendations move with current transits." },
                        { icon: Compass, title: "A map of your life", body: "Trace destinations you've visited and the planetary lines that still call to you." },
                    ].map((f, i) => (
                        <div key={f.title} className="animate-fade-up" data-testid={`landing-feature-${i}`}>
                            <f.icon strokeWidth={1.25} className="w-5 h-5 text-planet-sun" />
                            <div className="font-serif text-2xl mt-4">{f.title}</div>
                            <p className="text-ink-soft mt-2 leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            <footer className="border-t border-ink/10">
                <div className="sp-container py-10 text-xs text-ink-muted flex justify-between">
                    <span>© {new Date().getFullYear()} StarPath</span>
                    <span className="sp-overline">By the stars, for the curious</span>
                </div>
            </footer>
        </div>
    );
}
